import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function PricingMethodModal({ open, onClose, onSubmit }) {
    const [selectedMethod, setSelectedMethod] = useState('percentage-off-map');
    const [previewOnly, setPreviewOnly] = useState(false);
    const [debugMerchantSku, setDebugMerchantSku] = useState('');

    const pricingMethods = [
        {
            id: 'percentage-off-map',
            name: 'Percentage off MAP',
            description: 'Use MAP (Minimum Advertised Price) with condition-based percentage discounts',
            details: 'New: 100% MAP | Like New: 90% | Very Good: 80% | Good: 75% | Acceptable: 70%'
        },
        {
            id: 'lowest-price-per-condition',
            name: 'Lowest Price Per Condition',
            description: 'Use the lowest of MAP-based, competitive (lowest - $1), or Amazon threshold price',
            details: 'Compares MAP-based price vs competitive offer minus $1 vs Amazon competitive price threshold, uses lowest above threshold'
        },
        {
            id: 'debug-test-asin',
            name: 'DEBUG: Test Custom Merchant SKU',
            description: 'Debug pricing logic for a specific merchant SKU you provide',
            details: 'Shows detailed pricing calculations for troubleshooting. Enter a merchant SKU to test.'
        }
    ];

    const handleSubmit = () => {
        // Validate merchant SKU for debug mode
        if (selectedMethod === 'debug-test-asin') {
            if (!debugMerchantSku.trim()) {
                alert('Please enter a merchant SKU for debug mode');
                return;
            }
        }
        
        onSubmit(selectedMethod, previewOnly, debugMerchantSku.trim());
        onClose();
    };

    const handleCancel = () => {
        setSelectedMethod('percentage-off-map'); // Reset to default
        setPreviewOnly(false); // Reset preview flag
        setDebugMerchantSku(''); // Reset debug merchant SKU
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleCancel} className="relative z-50">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                        <div>
                            <div className="flex items-center justify-between">
                                <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                    Select Pricing Method
                                </Dialog.Title>
                                <button
                                    type="button"
                                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    onClick={handleCancel}
                                >
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            
                            <div className="mt-4">
                                <p className="text-sm text-gray-500 mb-6">
                                    Choose how prices should be calculated when syncing inventory with Amazon.
                                </p>
                                
                                <fieldset>
                                    <legend className="sr-only">Pricing methods</legend>
                                    <div className="space-y-3">
                                        {pricingMethods.map((method) => (
                                            <div 
                                                key={method.id} 
                                                className={`relative rounded-lg border p-4 cursor-pointer ${
                                                    selectedMethod === method.id 
                                                        ? 'border-indigo-200 bg-indigo-50' 
                                                        : 'border-gray-200 bg-white hover:bg-gray-50'
                                                }`}
                                                onClick={() => setSelectedMethod(method.id)}
                                            >
                                                <div className="flex items-start">
                                                    <div className="flex h-5 items-center">
                                                        <input
                                                            id={method.id}
                                                            name="pricing-method"
                                                            type="radio"
                                                            value={method.id}
                                                            checked={selectedMethod === method.id}
                                                            onChange={(e) => setSelectedMethod(e.target.value)}
                                                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                        />
                                                    </div>
                                                    <div className="ml-3 flex-1">
                                                        <label htmlFor={method.id} className="block text-sm font-medium text-gray-900 cursor-pointer">
                                                            {method.name}
                                                        </label>
                                                        <p className="text-sm text-gray-600 mt-1">{method.description}</p>
                                                        <p className="text-xs text-gray-500 mt-2">{method.details}</p>
                                                    </div>
                                                    {selectedMethod === method.id && (
                                                        <div className="absolute top-4 right-4">
                                                            <svg className="h-5 w-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </fieldset>
                                
                                {/* Merchant SKU Input for Debug Mode */}
                                {selectedMethod === 'debug-test-asin' && (
                                    <div className="mt-6 pt-4 border-t border-gray-200">
                                        <label htmlFor="debug-merchant-sku" className="block text-sm font-medium text-gray-900 mb-2">
                                            Enter Merchant SKU to Test
                                        </label>
                                        <input
                                            id="debug-merchant-sku"
                                            name="debug-merchant-sku"
                                            type="text"
                                            value={debugMerchantSku}
                                            onChange={(e) => setDebugMerchantSku(e.target.value)}
                                            placeholder="e.g., L8B-NJO-EHBC"
                                            className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            Enter a merchant SKU to analyze its pricing breakdown
                                        </p>
                                    </div>
                                )}
                                
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                    <div className="flex items-center">
                                        <input
                                            id="preview-only"
                                            name="preview-only"
                                            type="checkbox"
                                            checked={previewOnly}
                                            onChange={(e) => setPreviewOnly(e.target.checked)}
                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-600 border-gray-300 rounded"
                                        />
                                        <label htmlFor="preview-only" className="ml-2 text-sm text-gray-700">
                                            Preview output only (don't submit to Amazon)
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="mt-6 flex gap-3 sm:flex-row-reverse">
                            <button
                                type="button"
                                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:w-auto"
                                onClick={handleSubmit}
                            >
                                {previewOnly ? 'Preview' : 'Sync Inventory'}
                            </button>
                            <button
                                type="button"
                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </div>
        </Dialog>
    );
}